import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { SearchConfig } from '../../../model/api.model';
import { exportConfig } from '../../../model/dynamic-data-table.model';
import { CommonService } from '../../../services/common.service';
import { ExcelExportService } from '../../../services/excel-export.service';
import { CommonUtils } from '../../../utils/common.utils';
import { LoadingSkeletonComponent } from '../../../layout/loading-skeleton/loading-skeleton.component';
import { DownloadButtonComponent } from '../../download-button/download-button.component';

export interface SurveyAnswer {
  spinId: string;
  firstName: string;
  lastName: string;
  email: string;
  dealership: string;
  jobRole: string;
  question: string;
  answer: string;
}

const NO_RESPONSE = 'No response';

type SurveyAttendeeAnswers = {
  spinId: string;
  firstName: string;
  lastName: string;
  email: string;
  dealership: string;
  jobRole: string;
  answers: SurveyAnswer[];
};
 
@Component({
  selector: 'app-survey-answer-accordion',
  standalone: true,
  imports: [CommonModule, FormsModule, MatExpansionModule, MatFormFieldModule, MatIconModule, MatInputModule, MatPaginatorModule, LoadingSkeletonComponent, DownloadButtonComponent],
  templateUrl: './survey-answer-accordion.component.html',
  styleUrl: './survey-answer-accordion.component.scss',
})
export class SurveyAnswerAccordionComponent implements OnChanges, OnInit, OnDestroy {
  private readonly commonService = inject(CommonService);
  private readonly commonUtils = inject(CommonUtils);
  private readonly excelExportService = inject(ExcelExportService);
  @Input() data: SurveyAnswer[] = [];
  @Input() apiConfig?: SearchConfig;
  @Input() onLoadFetch = false;
  @Input() sectionTitle = 'All Attendee Responses';
  @Input() exportConfig: exportConfig = {
    fileName: 'All_Attendee_Responses',
    exportMessage: 'All Attendee Responses',
    schema: {
      spinId: 'Spin ID',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      dealership: 'Dealership',
      jobRole: 'Job Role',
      question: 'Question',
      answer: 'Answer',
    },
  };
  attendeeAnswers: SurveyAttendeeAnswers[] = [];
  filteredAttendeeAnswers: SurveyAttendeeAnswers[] = [];
  visibleAttendeeAnswers: SurveyAttendeeAnswers[] = [];
  pageIndex = 0;
  readonly pageSize = 10;
  isLoading = false;
  searchTerm = '';
  private readonly searchInput$ = new Subject<string>();
  private searchSubscription?: Subscription;
 
  ngOnInit(): void {
    this.searchSubscription = this.searchInput$.pipe(debounceTime(300)).subscribe(() => this.applyFilter());
    if (this.onLoadFetch && this.apiConfig) {
      this.isLoading = true;
      this.commonService.getDataFromDataSource(this.apiConfig, this.apiConfig.urlPath ?? '').then(response => {
        this.data = this.computeSurveyAnswers(response?.result ?? []);
        this.regroupAnswers();
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
      });
    }
  }
 
  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }
 
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !this.onLoadFetch) {
      this.regroupAnswers();
    }
  }
 
  onSearchInput(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement)?.value ?? '';
    this.searchInput$.next(this.searchTerm);
  }
 
  exportData(): void {
    const rawData = structuredClone(this.data) ?? [];
    if (rawData.length <= 0) {
      this.commonUtils.showMatSnackBar('Export Failed - No Data Found');
      return;
    }
    const formattedData = this.commonUtils.formatExcelData(this.exportConfig, rawData) ?? [];
    this.excelExportService.exportAsExcelFile(formattedData, this.exportConfig.fileName, this.exportConfig.descriptions);
    this.commonUtils.showMatSnackBar('File Exported Successfully');
  }
 
  private computeSurveyAnswers(result: any): SurveyAnswer[] {
    const rows = Array.isArray(result) ? result : (Array.isArray(result?.payload) ? result.payload : []);
    return rows.flatMap((item: any) => {
      const responses = item?.transformed_responses ?? [];
      const entries: Array<{ question: string; answer: any }> = Array.isArray(responses)
        ? responses.map((entry: any) => ({ question: String(entry?.question ?? ''), answer: entry?.answer }))
        : Object.keys(responses).map(question => ({ question, answer: responses[question] }));
      return entries
        .filter(entry => !!entry.question)
        .map(({ question, answer }) => ({
          spinId: item?.spin_id ?? item?.spinId ?? '',
          firstName: item?.first_name ?? '',
          lastName: item?.last_name ?? '',
          email: item?.email ?? '',
          dealership: item?.dealership ?? '',
          jobRole: item?.job_role ?? '',
          question,
          answer: answer !== undefined && answer !== null && String(answer).trim() !== '' ? String(answer) : NO_RESPONSE,
        }));
    });
  }
 
  private regroupAnswers(): void {
    const groupedAnswers = new Map<string, SurveyAttendeeAnswers>();
    for (const answer of this.data) {
      const key = answer.spinId
        ? `spin:${answer.spinId}`
        : `fallback:${answer.firstName}|${answer.lastName}|${answer.email}|${answer.dealership}|${answer.jobRole}`;
      const attendee = groupedAnswers.get(key) ?? { spinId: answer.spinId, firstName: answer.firstName, lastName: answer.lastName, email: answer.email, dealership: answer.dealership, jobRole: answer.jobRole, answers: [] };
      attendee.answers.push(answer);
      groupedAnswers.set(key, attendee);
    }
    this.attendeeAnswers = Array.from(groupedAnswers.values());
    this.applyFilter();
  }
 
  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredAttendeeAnswers = term
      ? this.attendeeAnswers.filter(attendee => this.matchesSearchTerm(attendee, term))
      : this.attendeeAnswers;
    this.pageIndex = 0;
    this.updateVisibleAttendees();
  }
 
  private matchesSearchTerm(attendee: SurveyAttendeeAnswers, term: string): boolean {
    const attendeeFields = [attendee.spinId, attendee.firstName, attendee.lastName, attendee.email, attendee.dealership, attendee.jobRole];
    if (attendeeFields.some(field => field?.toLowerCase().includes(term))) {
      return true;
    }
    return attendee.answers.some(answer => answer.question?.toLowerCase().includes(term) || answer.answer?.toLowerCase().includes(term));
  }
 
  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.updateVisibleAttendees();
  }
 
  private updateVisibleAttendees(): void {
    const startIndex = this.pageIndex * this.pageSize;
    this.visibleAttendeeAnswers = this.filteredAttendeeAnswers.slice(startIndex, startIndex + this.pageSize);
  }
}
